import { useMemo, useState } from 'react';
import { Plus, Search, Network as NetworkIcon } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useNetworkList,
  useCreatePrivateNetwork,
  useDeletePrivateNetwork,
  networkCidr,
  type Network,
} from '@/api/opennebula';
import { formatDateTime } from '@/lib/format-date';
import {
  NetworkLeasedCell,
  NetworkAttachedCell,
  NetworkRowActions,
  SimpleCreateDialog,
} from './networking-parts';

export function PrivateNetworks({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: networks = [], isLoading } = useNetworkList(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<Network | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return networks;
    return networks.filter((n) =>
      [n.name, n.id, networkCidr(n), n.address, n.type].some((f) => String(f ?? '').toLowerCase().includes(q)),
    );
  }, [networks, query]);

  const remove = useDeletePrivateNetwork(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Network deleted' });
      setDel(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<Network, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'cidr', header: 'CIDR', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{networkCidr(row.original) || row.original.address || '—'}</span> },
    { id: 'type', header: 'Type', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', textTransform: 'capitalize' }}>{row.original.type || '—'}</span> },
    // Leased + attached come from the per-network detail (not the list); each cell fetches it.
    { id: 'leased', header: 'Address leased', cell: ({ row }) => <NetworkLeasedCell serviceId={serviceId} networkId={row.original.id} /> },
    { id: 'attached', header: 'Attached VMs', cell: ({ row }) => <NetworkAttachedCell serviceId={serviceId} networkId={row.original.id} /> },
    { id: 'created', header: 'Created at', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.created_at ? formatDateTime(row.original.created_at) : '—'}</span> },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        // Delete is guarded per-row against the live attached count (backend rejects
        // deleting a network with instances still attached).
        <NetworkRowActions serviceId={serviceId} network={row.original} onDelete={setDel} />
      ),
    },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search networks" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} />}
        end={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New network</Button>}
      />
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(n) => String(n.id)}
          emptyState={
            networks.length === 0 ? (
              <EmptyState
                icon={<NetworkIcon size={28} />}
                title="No private networks yet"
                description="Create an internal network for your instances."
                actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New network</Button>}
              />
            ) : (
              'No networks match your search.'
            )
          }
        />
      )}
      <SimpleCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New private network"
        fields={[
          { key: 'name', label: 'Name', placeholder: 'internal-net' },
          { key: 'cidr', label: 'CIDR', placeholder: '10.0.0.0/24' },
        ]}
        onCreate={useCreatePrivateNetwork(serviceId, {
          onSuccess: () => { show({ tone: 'success', title: 'Network created' }); setCreateOpen(false); },
          onError: () => show({ tone: 'danger', title: 'Create failed' }),
        })}
      />
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete network?"
        description={`Remove “${del?.name || del?.id}”?`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </>
  );
}
